import { useMemo, useState, type FC, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import {
  closestCenter,
  defaultDropAnimationSideEffects,
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  type DropAnimation,
  type UniqueIdentifier,
} from '@dnd-kit/core'
//  import { restrictToVerticalAxis, restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS, type Transform } from '@dnd-kit/utilities'
import uniqid from 'uniqid'

import {
  SortableListContainer,
  SortableListStage,
  SortableListStageContentWrapper,
  SortableListStageHandleWrapper,
  SortableListStageItem,
  SortableListStageItemPlaceholder,
} from './SortableLists.styles.ts'

import { IconFAGripVerticalSolidRaw } from '../SVGIcons/IconFAGripVerticalSolidRaw.js'

interface SortableListItem {
  disableMove: boolean
  content: ReactNode
  handle?: ReactNode
}
interface onChangeArg {
  type: 'reorder'
  payload: {
    from: UniqueIdentifier
    to: UniqueIdentifier
  }
}

interface SortableListProps {
  className?: string
  'data-mu-test'?: string
  items: SortableListItem[]
  emptyState: ReactNode
  onChange: (changeArg: onChangeArg) => void
}

const DefaultEmptyState = <div>...</div>

const SortableListDefaultHandle = <IconFAGripVerticalSolidRaw height='18px' />

interface SortableItemProps {
  id: UniqueIdentifier
  item: SortableListItem
}

const SortableItem: FC<SortableItemProps> = ({ id, item }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform as unknown as Transform | null),
    transition,
  }

  return isDragging ? (
    <SortableListStageItemPlaceholder ref={setNodeRef} style={style} />
  ) : (
    <SortableListStageItem ref={setNodeRef} style={style}>
      <SortableListStageHandleWrapper {...listeners} {...attributes}>
        {item.handle ?? SortableListDefaultHandle}
      </SortableListStageHandleWrapper>
      <SortableListStageContentWrapper>
        {item.content}
      </SortableListStageContentWrapper>
    </SortableListStageItem>
  )
}

export const SortableList: FC<SortableListProps> = (
  {
    className,
    'data-mu-test': dataAttributeTest,
    items,
    emptyState,
    onChange,
  }: SortableListProps = {
    emptyState: DefaultEmptyState,
    items: [],
    onChange: () => {},
  },
) => {
  const [activeId, setActiveId] = useState<UniqueIdentifier | undefined>(
    undefined,
  )

  const sensors = useSensors(useSensor(PointerSensor))

  const memoItems = useMemo(() => {
    return items.map((o) => ({
      ...o,
      id: uniqid('sortable-list-'),
    }))
  }, [items])

  const handleDragStart = (event: DragStartEvent): void => {
    const { active } = event

    setActiveId(active.id)
  }

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event

    if (over !== null && active.id !== over.id) {
      const oldIndex = memoItems.findIndex((o) => o.id === active.id)
      const newIndex = memoItems.findIndex((o) => o.id === over.id)

      onChange({
        type: 'reorder',
        payload: {
          from: oldIndex,
          to: newIndex,
        },
      })
    }

    setActiveId(undefined)
  }

  const dropAnimationConfig: DropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
      styles: {
        active: {
          opacity: '0.5',
        },
      },
    }),
  }

  return (
    <SortableListContainer
      className={className}
      data-mu-test={dataAttributeTest}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        modifiers={
          [
            /* restrictToVerticalAxis, restrictToFirstScrollableAncestor */
          ]
        }
      >
        <SortableListStage>
          {memoItems.length === 0 && <>{emptyState}</>}
          <SortableContext
            items={memoItems}
            strategy={verticalListSortingStrategy}
          >
            {memoItems.map((o, index) => (
              <SortableItem key={o.id} id={o.id} item={o} />
            ))}
          </SortableContext>

          {createPortal(
            <DragOverlay
              adjustScale
              dropAnimation={dropAnimationConfig}
              zIndex={1100}
            >
              {activeId !== undefined ? (
                <SortableListStageItem isDragging>
                  <SortableListStageHandleWrapper>
                    {memoItems.find((o) => o.id === activeId)?.handle ??
                      SortableListDefaultHandle}
                  </SortableListStageHandleWrapper>
                  <SortableListStageContentWrapper>
                    {memoItems.find((o) => o.id === activeId)?.content}
                  </SortableListStageContentWrapper>
                </SortableListStageItem>
              ) : undefined}
            </DragOverlay>,
            document.body,
          )}
        </SortableListStage>
      </DndContext>
    </SortableListContainer>
  )
}

//  https://github.com/clauderic/dnd-kit/blob/master/stories/2%20-%20Presets/Sortable/Sortable.tsx#L67
